import React, { forwardRef } from 'react';

import { Select } from '@aline/common';
import { ButtonBase } from '@mui/material';
import { Link } from 'react-router-dom';
import { LeftNavIcon, LeftNavItemLabel } from 'src/components';
import { openLinkFromAnchorClick } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import type { AppSection } from '../ui-interfaces';
import { getRouteForAppSection, LeftNavSection } from '../ui-interfaces';

interface StyleProps {
  selected: boolean;
}

export const useLeftNavItemStyles = makeStyles<StyleProps>()((theme, { selected }) => ({
  content: {
    '&:hover': {
      backgroundColor: '#2E3F5F',
    },
    alignItems: 'center',
    backgroundColor: selected ? '#2E3F5F' : undefined,
    borderRadius: 8,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    justifyContent: 'center',
    padding: theme.spacing(0.5),
    position: 'relative',
    textDecoration: 'none',
    transition: theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
    width: '100%',
  },
}));

interface LeftNavItemProps {
  section: AppSection;
  selectedNodeId: string;
}

export const LeftNavItem = forwardRef<HTMLAnchorElement, LeftNavItemProps>(function LeftNavItem(
  { section, selectedNodeId },
  ref
): JSX.Element {
  const path = getRouteForAppSection(section);
  const selected = selectedNodeId === path;
  const { classes } = useLeftNavItemStyles({ selected });

  return (
    <ButtonBase
      ref={ref}
      className={classes.content}
      component={Link}
      data-cy={getDataCy(section)}
      to={path}
      onClick={e => openLinkFromAnchorClick(e, path, { newTab: false })}
    >
      <LeftNavIcon section={section} selected={selected} />
      <LeftNavItemLabel section={section} selected={selected} />
    </ButtonBase>
  );
});

function getDataCy(section: LeftNavSection) {
  switch (section) {
    case LeftNavSection.ADMIN:
      return Select.leftNav.admin.icon;
    case LeftNavSection.AI_CHAT:
      return Select.leftNav.aiChat.icon;
    case LeftNavSection.COMMUNITY:
      return Select.leftNav.community.icon;
    case LeftNavSection.DASHBOARD:
      return Select.leftNav.dashboard.icon;
    case LeftNavSection.GET_STARTED:
      return Select.leftNav.getStarted.icon;
    case LeftNavSection.IDENTIFIERS:
      return Select.leftNav.identifiers.icon;
    case LeftNavSection.PARTIES:
      return Select.leftNav.parties.icon;
    case LeftNavSection.PLAYBOOKS:
      return Select.leftNav.playbooks.icon;
    case LeftNavSection.REPORTS:
      return Select.leftNav.reports.icon;
    case LeftNavSection.REPOSITORY:
      return Select.leftNav.repository.icon;
    case LeftNavSection.WORKFLOWS:
      return Select.leftNav.workflows.icon;
    case LeftNavSection.TEMPLATES:
      return Select.leftNav.templates.icon;
  }
}
